/* eslint-disable */
import React, { useState, useEffect } from "react";
import Axios from "utils/Axios";
import MKButton from "components/MKButton";
import { ButtonGroup, Button } from "@mui/material";
import Icon from "@mui/material/Icon";
import { useNavigate } from "react-router-dom";
import coverImageDefault from "../../../assets/images/groove-sign-up.jpg";
import Avatar from "@mui/material/Avatar";
import truncate from "truncate-html";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import MKInput from "components/MKInput";
import Pagination from "@mui/material/Pagination";

/**
 * @function
 * @description This component renders a list of events, grouped by date.
 *              It fetches the events data from the API and displays them in a
 *              paginated grid. The user can filter the events by status (approved
 *              or pending) and search for events by name. The user can also click
 *              on an event to view its details.
 * @param {Object} props Component props
 * @returns {ReactElement} The rendered component
 */
function Events() {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const imageUrl = (url) => process.env.REACT_APP_BASE_IMAGE_URL + url;

  const gotoEventDetails = (eventId) => {
    navigate(`/admin/events/single/${eventId}`);
  };

  const truncateHtml = (htmlContent, length = 100) => {
    return truncate(htmlContent, {
      length: length,
      ellipsis: "...",
      keepImageTag: true,
    });
  };

  // Fetch events with pagination
  const fetchEvents = (pageNum = 1, filterType = "All", searchQuery = "") => {
    setLoading(true);
    let url = `?type=adminEvent&page=${pageNum}&limit=${limit}`;
    
    // If we're filtering by status, we need to add that to the API request
    if (filterType === "Approved") {
      url += "&status=1";
    } else if (filterType === "Pending") {
      url += "&status=0";
    }
    
    // Add search parameter if provided
    if (searchQuery) {
      url += `&search=${searchQuery}`;
    }

    Axios.get(url)
      .then((response) => {
        const eventData = response.data.data;
        const flattenedEvents = eventData.flatMap((dateGroup) =>
          dateGroup.events?.map((event) => ({
            id: event.id,
            title: event.title,
            description: event.description,
            time: event.time,
            image: event.image,
            location: event.location,
            ticketPrice: event.ticketPrice,
            date: event.eventDate,
            is_active: event.is_active,
          }))
        );
        
        setEvents(flattenedEvents);
        
        // Set pagination data from the API response
        if (response.data.pagination) {
          setTotalItems(response.data.pagination.total);
          setTotalPages(response.data.pagination.totalPages);
          setPage(response.data.pagination.page);
        }
        
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch events data", error);
        setLoading(false);
      });
  };

  // Initial fetch
  useEffect(() => {
    fetchEvents(1, filter, search);
  }, [token]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1); // Reset to first page on filter change
    fetchEvents(1, newFilter, search);
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    if (value.length >= 2) {
      setPage(1); // Reset to first page on search
      fetchEvents(1, filter, value);
    } else if (value.length === 0) {
      setPage(1);
      fetchEvents(1, filter, "");
    }
  };

  // Handle page change for pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchEvents(newPage, filter, search);
  };

  return (
    <div className="promoterEvents">
      <div className="AddEvent flex justify-between max-sm:flex-col-reverse flex-col">
        <div>
          <div className="lg:mx-12 my-2">
            <div className="text-2xl font-bold py-0 text-gray-800">Events</div>
            <div className="text-sm font-light py-2 text-gray-600">
              All the available events will be listed here, according to their state of approval.
            </div>
          </div>
        </div>
        <div className="addEventText max-sm:w-full flex justify-end max-sm:flex-col max-sm:justify-start items-center gap-4">
          <div className="searchBar w-64 max-sm:w-full bg-white rounded-lg">
            <MKInput
              color="white"
              value={search}
              onChange={handleSearchChange}
              fullWidth
              size="small"
              placeholder="Search events..."
            />
          </div>
          <ButtonGroup variant="contained" color="info" className="mr-4 max-sm:mr-0 max-sm:mt-2">
            <Button
              sx={{
                backgroundColor: filter === "All" ? "#4E0249" : "#ffffff",
                color: filter === "All" ? "white" : "#4E0249",
              }}
              onClick={() => handleFilterChange("All")}
            >
              All
            </Button>
            <Button
              sx={{
                backgroundColor: filter === "Approved" ? "#4E0249" : "#ffffff",
                color: filter === "Approved" ? "white" : "#4E0249",
              }}
              onClick={() => handleFilterChange("Approved")}
            >
              Approved
            </Button>
            <Button
              sx={{
                backgroundColor: filter === "Pending" ? "#4E0249" : "#ffffff",
                color: filter === "Pending" ? "white" : "#4E0249",
              }}
              onClick={() => handleFilterChange("Pending")}
            >
              Pending
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p>Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <p>No events found.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 p-4">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white text-gray-800 p-4 rounded-lg shadow-lg relative"
            >
              <div className="flex gap-x-8" onClick={() => gotoEventDetails(event.id)} style={{ cursor: 'pointer' }}>
                <Avatar
                  alt={event.title}
                  src={imageUrl(event.image) || coverImageDefault}
                  sx={{ width: 56, height: 56 }}
                  variant="rounded"
                />
                <div className="">
                  <h2 className="text-lg font-bold">{event.title}</h2>
                  <p className="text-xs font-thin text-green-600">{event.location}</p>
                  {event.is_active === "0" ? (
                    <div>
                      <p className="text-xs font-thin text-white bg-yellow-600 max-w-fit px-2 py-0.5 rounded-md">
                        Pending
                      </p>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div className="mt-10" onClick={() => gotoEventDetails(event.id)} style={{ cursor: 'pointer' }}>
                <p
                  className="text-sm text-gray-600 text-wrap overflow-hidden break-words"
                  dangerouslySetInnerHTML={{
                    __html: truncateHtml(event.description, 100),
                  }}
                ></p>
              </div>
              <div className="flex justify-between items-center mt-5">
                <p className="text-xs text-gray-500" onClick={() => gotoEventDetails(event.id)} style={{ cursor: 'pointer' }}>
                  Event Date: {event.date + " " + event.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalItems > 0 && (
        <div className="flex flex-col items-center mt-6 mb-4">
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange} 
            color="primary" 
            size="large"
          />
          <div className="text-center text-sm mt-2">
            Showing page {page} of {totalPages} ({totalItems} total events)
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
